import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { ApiError } from '../api/client'
import {
  installStateLabel,
  requestRustdeskInstall,
  rustdeskInstallQuery,
  type RustdeskInstallStatus,
} from '../api/remoteConnectRustdesk'
import { toast } from '../ui/notify'

/**
 * Instalarea RustDesk la cerere, vazuta din portal.
 *
 * Doua locuri o folosesc:
 *
 *  - pagina de lansare (Connect) pe un aparat fara RustDesk: `auto` - cererea
 *    pleaca singura, o data (omul a apasat deja Connect), apoi progresul se
 *    citeste la 3 secunde pana apare ID-ul, si pagina se reincarca singura cu
 *    butonul "Open in RustDesk";
 *  - fisa aparatului (Remote Operations): butonul "Install RustDesk", vizibil
 *    pe "On demand" cat timp aparatul n-are RustDesk.
 *
 * Starile vin de la agent (POST /rustdesk/status in ph4.api): downloading,
 * installing, installed, failed. Cererea marcheaza aparatul si trimite FRC, ca
 * agentul sa-si reciteasca configul acum.
 */

function failed(error: unknown): string {
  return error instanceof ApiError && error.message ? error.message : 'Something went wrong.'
}

function inProgress(status: RustdeskInstallStatus | undefined): boolean {
  return !!status && status.requested && !status.installed && status.installState !== 'failed'
}

function Progress({ status }: { status: RustdeskInstallStatus }) {
  if (status.installState === 'failed') {
    return (
      <span className="text-danger rd-install-failed">
        <i className="fas fa-exclamation-triangle" aria-hidden="true"></i> Installing RustDesk failed
        {status.installError !== '' ? `: ${status.installError}` : '.'}
      </span>
    )
  }
  const state = status.installState
  const label =
    state === 'downloading' || state === 'installing'
      ? `${installStateLabel(state)} RustDesk…`
      : 'Waiting for the agent to pick up the request…'
  return (
    <span className="text-info rd-install-progress">
      <i className="fas fa-spinner fa-spin" aria-hidden="true"></i> {label}
    </span>
  )
}

/** Pe pagina de lansare: cere singura instalarea si asteapta ID-ul. */
export function RustdeskInstallWait({ deviceId, onInstalled }: { deviceId: number; onInstalled(): void }) {
  const query = useQueryClient()
  const asked = useRef(false)
  const [error, setError] = useState('')
  const [polling, setPolling] = useState(false)
  const status = useQuery(rustdeskInstallQuery(deviceId, polling))
  const data = status.data

  const ask = () => {
    asked.current = true
    setError('')
    void requestRustdeskInstall(deviceId)
      .then((next) => {
        query.setQueryData(rustdeskInstallQuery(deviceId).queryKey, next)
        setPolling(true)
      })
      .catch((e: unknown) => setError(failed(e)))
  }

  useEffect(() => {
    if (!data || !data.ready || data.accessDisabled) return
    if (data.installed) {
      setPolling(false)
      onInstalled()
      return
    }
    if (inProgress(data)) {
      setPolling(true)
      return
    }
    if (!asked.current && data.installState !== 'failed') ask()
  }, [data])

  if (!data || !data.ready) return null

  return (
    <div className="rdl-note rd-install-wait">
      {error !== '' ? (
        <span className="text-danger">{error}</span>
      ) : (
        <Progress status={data} />
      )}
      {(data.installState === 'failed' || error !== '') && (
        <>
          {' '}
          <button type="button" className="btn btn-default btn-xs rd-install-retry" onClick={ask}>
            Try again
          </button>
        </>
      )}
      <div className="text-muted small">The connection details appear here as soon as RustDesk reports its ID.</div>
    </div>
  )
}

/** Pe fisa aparatului: butonul "Install RustDesk" pe "On demand", si progresul. */
export function RustdeskInstallButton({ deviceId }: { deviceId: number }) {
  const query = useQueryClient()
  const [polling, setPolling] = useState(false)
  const status = useQuery(rustdeskInstallQuery(deviceId, polling))
  const data = status.data

  useEffect(() => {
    setPolling(inProgress(data))
  }, [data])

  if (!data || !data.ready || data.accessDisabled) return null

  /* Instalat: numai serverul pe care sta acum (din `status.active_server`) -
     agentul il alege pe primul din lista care raspunde. */
  if (data.installed) {
    return data.activeServer !== '' ? (
      <span className="text-muted small rd-active-server" title="The RustDesk server this device is using now">
        <i className="fas fa-server" aria-hidden="true"></i> RustDesk server: {data.activeServer}
      </span>
    ) : null
  }

  const busy = inProgress(data)
  if (data.installMode !== 'demand' && !data.requested) return null

  return (
    <span className="rd-install-button">
      {!busy && (
        <button
          className="btn btn-sm btn-default"
          type="button"
          id="rdInstall"
          onClick={() => {
            void requestRustdeskInstall(deviceId)
              .then((next) => {
                query.setQueryData(rustdeskInstallQuery(deviceId).queryKey, next)
                toast('RustDesk will be installed on this device.', 'Install RustDesk')
              })
              .catch((e: unknown) => toast(failed(e), 'Install RustDesk', 'error'))
          }}
        >
          <i className="fas fa-download" aria-hidden="true"></i> Install RustDesk
        </button>
      )}{' '}
      {data.requested && <Progress status={data} />}
    </span>
  )
}
